import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Search, X, Loader2 } from 'lucide-react';
import { searchDeals } from '../../services/api';

const SCORE_CONFIG = {
  'STRONG BUY': { icon: TrendingUp,   label: 'STRONG BUY' },
  'BUY':        { icon: TrendingUp,   label: 'BUY'        },
  'HOLD':       { icon: Minus,        label: 'HOLD'       },
  'PASS':       { icon: TrendingDown, label: 'PASS'       },
};

export default function Module07({ data }) {
  const { deals: savedDeals = [], run_date, screened } = data;
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [liveDeals, setLiveDeals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [liveMeta, setLiveMeta] = useState(null);

  const showingLive = Boolean(query && liveDeals);
  const displayDeals = showingLive ? liveDeals : savedDeals;

  async function submit(e) {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;

    setQuery(q);
    setError('');
    setLoading(true);
    setLiveDeals(null);
    setLiveMeta(null);

    try {
      const result = await searchDeals(q);
      setLiveDeals(result.deals || []);
      setLiveMeta({
        message: result.message,
        listing_count: result.listing_count,
        source: result.source,
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Search failed';
      setError(msg);
      setLiveDeals([]);
    } finally {
      setLoading(false);
    }
  }

  function clear() {
    setInput('');
    setQuery('');
    setLiveDeals(null);
    setLiveMeta(null);
    setError('');
  }

  return (
    <div className="space-y-5">

      {/* Area search */}
      <form onSubmit={submit} className="space-y-2">
        <div className="flex items-center gap-2 border border-white/10 px-3 py-2.5 focus-within:border-white/30 transition-colors">
          <Search size={14} className="text-gray-600 shrink-0" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search another area — city, state, or ZIP"
            className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none tracking-wide"
            disabled={loading}
          />
          {(input || query) && !loading && (
            <button type="button" onClick={clear} className="text-gray-600 hover:text-white transition-colors shrink-0">
              <X size={14} />
            </button>
          )}
          <button type="submit" disabled={loading || !input.trim()}
            className="text-[9px] tracking-[0.2em] uppercase font-medium px-3 py-1 shrink-0 disabled:opacity-40"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            {loading ? '…' : 'Search'}
          </button>
        </div>
        <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600">
          Live RentCast search — try “33602”, “Tampa, FL”, or “34950”.
        </p>
      </form>

      {loading && (
        <div className="flex items-center gap-3 border border-white/8 px-4 py-5">
          <Loader2 size={16} className="animate-spin" style={{ color: 'var(--gold)' }} />
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">Scanning RentCast…</p>
            <p className="text-[9px] text-gray-600 mt-1">Fetching listings and scoring with AI — may take 15–30 sec.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="border border-red-500/20 px-4 py-3">
          <p className="text-[10px] tracking-[0.15em] uppercase text-red-400">Search failed</p>
          <p className="text-[11px] text-gray-500 mt-1">{error}</p>
        </div>
      )}

      <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">
        {showingLive
          ? liveMeta?.listing_count
            ? `${displayDeals.length} scored · ${liveMeta.listing_count} listings · ${query}`
            : `${displayDeals.length} result${displayDeals.length === 1 ? '' : 's'} · ${query}`
          : screened
            ? `${displayDeals.length} of ${screened} screened · ${run_date}`
            : `Saved targets · ${run_date}`}
      </p>

      {showingLive && liveMeta?.source === 'rentcast' && displayDeals.length > 0 && (
        <p className="text-[9px] tracking-[0.15em] uppercase" style={{ color: 'var(--gold-dim)' }}>
          Live from RentCast API
        </p>
      )}

      {showingLive && displayDeals.length === 0 && !loading && !error && (
        <div className="border-l-2 pl-4 py-2" style={{ borderColor: 'var(--gold-dim)' }}>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            {liveMeta?.message || `No active listings found for “${query}”. Try a nearby ZIP code.`}
          </p>
        </div>
      )}

      {!loading && displayDeals.map((deal, i) => <DealCard key={`${deal.address}-${i}`} deal={deal} rank={i + 1} />)}

      {!query && !loading && savedDeals.length === 0 && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">
          No deals found for today.
        </p>
      )}
    </div>
  );
}

function DealCard({ deal, rank }) {
  const cfg = SCORE_CONFIG[deal.score] || SCORE_CONFIG['HOLD'];
  const Icon = cfg.icon;
  const isTopPick = deal.score === 'STRONG BUY';

  return (
    <div className={`border pb-5 mb-5 space-y-4 ${isTopPick ? 'border-white/20' : 'border-white/8'}`}>

      <div className="flex items-start justify-between gap-2 p-4 border-b border-white/8">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="text-[9px] tracking-[0.2em] uppercase text-gray-600">#{rank}</span>
            <p className="font-medium tracking-wide text-sm">{deal.address}</p>
          </div>
          <p className="text-[10px] tracking-[0.1em] uppercase text-gray-600 mt-0.5 ml-6">
            {deal.city}, {deal.state} {deal.zip}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0"
          style={{ color: isTopPick ? 'var(--gold)' : deal.score === 'BUY' ? '#ccc' : '#555' }}>
          <Icon size={12} />
          <span className="text-[9px] tracking-[0.2em] uppercase font-medium">{cfg.label}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4">
        {[
          { label: 'Price', value: formatCurrency(deal.price) },
          {
            // A modelled rent carries a confidence range; the 0.75%-of-price
            // fallback does not, and is marked so the two are never confused.
            label: 'Rent',
            value: formatCurrency(deal.est_rent) + '/mo',
            note: deal.rent_is_estimated
              ? 'rule of thumb'
              : deal.rent_range
                ? `${formatCurrency(deal.rent_range[0])}–${formatCurrency(deal.rent_range[1])}`
                : null,
          },
          { label: 'Yield', value: deal.yield_pct + '%', gold: true },
        ].map(({ label, value, note, gold }) => (
          <div key={label} className="border border-white/8 p-3 text-center">
            <p className="text-sm font-light" style={gold ? { color: 'var(--gold)' } : {}}>{value}</p>
            <p className="text-[9px] tracking-[0.2em] uppercase text-gray-600 mt-1">{label}</p>
            {note && <p className="text-[8px] text-gray-700 mt-0.5 leading-tight">{note}</p>}
          </div>
        ))}
      </div>

      <div className="px-4 space-y-3">
        {deal.pros?.length > 0 && (
          <div>
            <p className="text-[9px] tracking-[0.25em] uppercase text-gray-600 mb-2">Pros</p>
            <ul className="space-y-1.5">
              {deal.pros.map((p, i) => (
                <li key={i} className="text-[11px] text-gray-400 flex gap-3 leading-relaxed">
                  <span style={{ color: 'var(--gold)' }} className="shrink-0">+</span>{p}
                </li>
              ))}
            </ul>
          </div>
        )}
        {deal.risks?.length > 0 && (
          <div>
            <p className="text-[9px] tracking-[0.25em] uppercase text-gray-600 mb-2">Risks</p>
            <ul className="space-y-1.5">
              {deal.risks.map((r, i) => (
                <li key={i} className="text-[11px] text-gray-400 flex gap-3 leading-relaxed">
                  <span className="text-gray-600 shrink-0">−</span>{r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {deal.verdict && (
        <div className="mx-4 border-l-2 pl-4 py-1" style={{ borderColor: 'var(--gold-dim)' }}>
          <p className="text-[9px] tracking-[0.2em] uppercase text-gray-600 mb-1">AI Analysis</p>
          <p className="text-[11px] text-gray-400 leading-relaxed">{deal.verdict}</p>
        </div>
      )}
    </div>
  );
}

function formatCurrency(n) {
  if (!n) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}
